import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../config/assets';

export interface Person {
  id: string;
  external_id: string;
  name: string;
  photo_url: string;
  speciality: string;
}

export interface CenterTextBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

export type CenterTextBehavior = 'avoid' | 'fade';

export interface FloatingPhotoProps {
  person: Person;
  speed?: number;
  isNew?: boolean;
  shouldEnter?: boolean;
  shouldExit?: boolean;
  initialSizePercentage?: number;
  centerTextBounds?: CenterTextBounds; // Bounds do CenterText para evitar
  minPhotoSize?: number;
  maxPhotoSize?: number;
  newPhotoSize?: number;
  newPhotoHighlightTime?: number; // Tempo em segundos que a foto nova fica maior
  onExitComplete?: (personId: string) => void;
  onImageError?: (personId: string) => void; // Callback quando imagem falha ao carregar
  centerTextBehavior?: CenterTextBehavior; // 'avoid' (não passa) ou 'fade' (passa e perde opacidade)
  managedPosition?: { x: number; y: number }; // Posição inicial gerenciada
  onPositionUpdate?: (personId: string, position: { x: number; y: number }, velocity: { x: number; y: number }) => { position: { x: number; y: number }; velocityCorrection?: { x: number; y: number } } | undefined; // Callback para atualizar posição no manager
  // Performance props
  targetFPS?: number; // FPS desejado (padrão: 60)
  enableThrottling?: boolean; // Se deve limitar o framerate (padrão: false)
  animationQuality?: 'low' | 'medium' | 'high'; // Qualidade da animação (padrão: 'high')
  // Participant name settings
  participantNameFontSize?: number; // Porcentagem do tamanho da foto para fonte do nome (padrão: 15 = 15%)
}

const FloatingPhoto: React.FC<FloatingPhotoProps> = ({
  person,
  speed = 20,
  isNew = false,
  shouldEnter = false,
  shouldExit = false,
  initialSizePercentage = 0,
  centerTextBounds,
  minPhotoSize = 10,
  maxPhotoSize = 14,
  newPhotoSize = 24,
  newPhotoHighlightTime = 6, // 6 segundos por padrão
  onExitComplete,
  onImageError,
  managedPosition,
  onPositionUpdate,
  // Performance props
  targetFPS = 60,
  enableThrottling = false,
  animationQuality = 'high',
  centerTextBehavior = 'fade',
  // Participant name settings
  participantNameFontSize = 15
}) => {

  const isNewRef = useRef(isNew);

  const getInitialSize = () => {
    if (isNewRef.current) {
      return newPhotoSize;
    }
    if (initialSizePercentage) return initialSizePercentage;
    return Math.random() * (maxPhotoSize - minPhotoSize) + minPhotoSize; // 10-14%
  };

  // Estados para posição e tamanho
  const initialSize = getInitialSize();
  const initialPhotoSize = Math.round((initialSize / 100) * window.innerHeight);
  const photoSize = useRef(initialPhotoSize);
  const [currentSizePercentage, setCurrentSizePercentage] = useState(initialSize);

  const [isEntering, setIsEntering] = useState(shouldEnter || isNew);
  const [isExiting, setIsExiting] = useState(false);
  const [opacity, setOpacity] = useState((shouldEnter || isNew) ? 0 : 1); // Começar sempre com opacidade 0 para novos elementos
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isInsideCenterTextRef = useRef(false); // Track if inside CenterText area
  const lastOpacityChangeRef = useRef(0); // Track last opacity change time
  const baseOpacityRef = useRef(1); // Track the base opacity when not in center text

  const hasExitedRef = useRef(false);

  // Refs para controle de tamanho
  const targetSizeRef = useRef(currentSizePercentage);
  const sizeTransitionRef = useRef({
    isTransitioning: false,
    transitionStartTime: 0,
    startSize: currentSizePercentage,
    duration: 2000,
    stayDuration: 0,
    nextChangeTime: Date.now() + 10000 // 10s
  });

  if (isNewRef.current) {
    sizeTransitionRef.current.nextChangeTime = Date.now() + (newPhotoHighlightTime * 1000); // Tempo configurável
  };

  const [imageLoaded, setImageLoaded] = useState(false);
  const [position, setPosition] = useState(managedPosition || { x: 0, y: 0 }); // Usar posição gerenciada se disponível
  const velocityRef = useRef({ 
    x: (Math.random() - 0.5) * speed * 1, 
    y: (Math.random() - 0.5) * speed * 1 
  });
  const animationRef = useRef<number>();
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTimeRef = useRef<number>(0);
  const directionChangeTimerRef = useRef<number>(0);
  const targetVelocityRef = useRef({ 
    x: velocityRef.current.x, 
    y: velocityRef.current.y 
  });

  // Refs para suavização de correções de posição
  const targetPositionRef = useRef({ x: 0, y: 0 });
  const isBeingCorrectedRef = useRef(false);

  // Controle de framerate
  const frameInterval = enableThrottling ? (1000 / targetFPS) : 0; // Intervalo entre frames em ms
  const lastFrameTimeRef = useRef<number>(0);

  // Configurações baseadas na qualidade da animação
  const getQualitySettings = () => {
    switch (animationQuality) {
      case 'low':
        return {
          lerpFactor: 0.02, // Mais suave mesmo em qualidade baixa
          directionChangeInterval: { min: 2, max: 4 },
          sizeTransitionDuration: 1000,
          opacityTransitionClass: 'opacity 0.15s ease-out',
          useSubPixelRendering: true // Sempre usar sub-pixel para suavidade
        };
      case 'medium':
        return {
          lerpFactor: 0.01, // Mais suave
          directionChangeInterval: { min: 3, max: 6 },
          sizeTransitionDuration: 1500,
          opacityTransitionClass: 'opacity 0.2s ease-in-out',
          useSubPixelRendering: true // Sempre usar sub-pixel
        };
      case 'high':
      default:
        return {
          lerpFactor: 0.005, // Ultra-suave para movimento premium
          directionChangeInterval: { min: 4, max: 10 },
          sizeTransitionDuration: 2000,
          opacityTransitionClass: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Curva mais suave
          useSubPixelRendering: true // Sub-pixel máximo para suavidade
        };
    }
  };

  const qualitySettings = getQualitySettings();

  // Função para calcular dimensões da moldura de forma mais suave
  const getFrameDimensions = () => {
    const baseSize = photoSize.current; // Usar valor preciso
    
    // Calcular offset e tamanho da moldura de forma mais precisa
    const frameOffset = baseSize * 0.022; // 2.2% como offset preciso
    const frameSize = baseSize + (2 * frameOffset); // Tamanho total preciso
    
    // Debug: log apenas se o tamanho mudou significativamente (desenvolvimento)
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
      console.log(`Frame dimensions - Base: ${baseSize.toFixed(1)}px, Offset: ${frameOffset.toFixed(1)}px, Frame: ${frameSize.toFixed(1)}px`);
    }
    
    return {
      top: -frameOffset,
      left: -frameOffset,
      width: frameSize,
      height: frameSize
    };
  };

  // Função para verificar se está colidindo com a área do CenterText
  const isCollidingWithCenterText = (x: number, y: number) => {
    if (!centerTextBounds) return false;
    
    const margin = -10; // Margem ainda maior para segurança máxima
    const photoWidth = photoSize.current;
    const photoHeight = photoSize.current + 40; // Considerando o nome abaixo
    
    // Verificar se qualquer parte da foto (incluindo nome) está na área protegida
    const photoLeft = x;
    const photoRight = x + photoWidth;
    const photoTop = y;
    const photoBottom = y + photoHeight;
    
    const textLeft = centerTextBounds.left - margin;
    const textRight = centerTextBounds.right + margin;
    const textTop = centerTextBounds.top - margin;
    const textBottom = centerTextBounds.bottom + margin;
    
    // Verificar sobreposição em ambos os eixos
    const overlapsHorizontally = photoRight > textLeft && photoLeft < textRight;
    const overlapsVertically = photoBottom > textTop && photoTop < textBottom;
    
    return overlapsHorizontally && overlapsVertically;
  };

  // Função para gerar posição inicial segura (fora da área do centerText)
  const generateSafeInitialPosition = () => {
    const totalHeight = initialPhotoSize + 40; // foto + espaçamento + nome
    let attempts = 0;
    const maxAttempts = 50;
    
    while (attempts < maxAttempts) {
      let x, y;
      
      if (centerTextBounds) {
        // Se há centerText, priorizar áreas superior e inferior
        const screenHeight = window.innerHeight;
        const screenWidth = window.innerWidth;
        
        // Definir zonas seguras: superior (0 a 30% da tela) e inferior (70% a 100% da tela)
        const upperZoneHeight = screenHeight * 0.3;
        const lowerZoneStart = screenHeight * 0.7;
        const lowerZoneHeight = screenHeight - lowerZoneStart - totalHeight;
        
        // Escolher aleatoriamente entre zona superior ou inferior
        const useUpperZone = Math.random() < 0.5;
        
        if (useUpperZone && upperZoneHeight > totalHeight) {
          // Posicionar na zona superior
          x = Math.random() * (screenWidth - initialPhotoSize);
          y = Math.random() * (upperZoneHeight - totalHeight);
        } else if (lowerZoneHeight > 0) {
          // Posicionar na zona inferior
          x = Math.random() * (screenWidth - initialPhotoSize);
          y = lowerZoneStart + Math.random() * lowerZoneHeight;
        } else {
          // Fallback: usar toda a tela se as zonas forem muito pequenas
          x = Math.random() * (screenWidth - initialPhotoSize);
          y = Math.random() * (screenHeight - totalHeight);
        }
      } else {
        // Se não há centerText, usar toda a tela
        x = Math.random() * (window.innerWidth - initialPhotoSize);
        y = Math.random() * (window.innerHeight - totalHeight);
      }
      
      // Verificar se esta posição está segura (não colide com centerText)
      if (!centerTextBounds || !isCollidingWithCenterText(x, y)) {
        return { x, y };
      }
      
      attempts++;
    }
    
    // Fallback: posições distribuídas nas bordas superior e inferior
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const margin = 20;
    
    const fallbackPositions = [
      // Zona superior - esquerda, centro, direita
      { x: margin, y: margin },
      { x: screenWidth / 2 - initialPhotoSize / 2, y: margin },
      { x: screenWidth - initialPhotoSize - margin, y: margin },
      // Zona superior-meio
      { x: screenWidth * 0.25 - initialPhotoSize / 2, y: margin },
      { x: screenWidth * 0.75 - initialPhotoSize / 2, y: margin },
      
      // Zona inferior - esquerda, centro, direita
      { x: margin, y: screenHeight - totalHeight - margin },
      { x: screenWidth / 2 - initialPhotoSize / 2, y: screenHeight - totalHeight - margin },
      { x: screenWidth - initialPhotoSize - margin, y: screenHeight - totalHeight - margin },
      // Zona inferior-meio
      { x: screenWidth * 0.25 - initialPhotoSize / 2, y: screenHeight - totalHeight - margin },
      { x: screenWidth * 0.75 - initialPhotoSize / 2, y: screenHeight - totalHeight - margin },
      
      // Laterais (meio da tela verticalmente)
      { x: margin, y: screenHeight / 2 - totalHeight / 2 },
      { x: screenWidth - initialPhotoSize - margin, y: screenHeight / 2 - totalHeight / 2 }
    ];
    
    return fallbackPositions[Math.floor(Math.random() * fallbackPositions.length)];
  };

  // Função para gerar nova direção aleatória
  const generateNewDirection = () => {
    const angle = Math.random() * Math.PI * 2;
    const magnitude = speed * 1;
    const newDirection = {
      x: Math.cos(angle) * magnitude,
      y: Math.sin(angle) * magnitude
    };
    
    targetVelocityRef.current = newDirection;
  };

  // Função de animação
  const animate = (currentTime: number) => {
    // Throttle de framerate (só se habilitado)
    if (enableThrottling && (currentTime - lastFrameTimeRef.current < frameInterval)) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }
    lastFrameTimeRef.current = currentTime;

    if (!lastTimeRef.current) {
      lastTimeRef.current = currentTime;
    }

    const deltaTime = (currentTime - lastTimeRef.current) / 1000; // Convert to seconds
    lastTimeRef.current = currentTime;

    // Atualizar timer para mudança de direção
    directionChangeTimerRef.current += deltaTime;

    // Mudar direção aleatoriamente com base na qualidade
    const { min, max } = qualitySettings.directionChangeInterval;
    if (directionChangeTimerRef.current > min + Math.random() * (max - min)) {
      generateNewDirection();
      directionChangeTimerRef.current = 0;
    }

    // Interpolar suavemente a velocidade atual para a velocidade alvo
    velocityRef.current.x += (targetVelocityRef.current.x - velocityRef.current.x) * qualitySettings.lerpFactor;
    velocityRef.current.y += (targetVelocityRef.current.y - velocityRef.current.y) * qualitySettings.lerpFactor;

    setPosition(prevPosition => {
      let newX = prevPosition.x + velocityRef.current.x * deltaTime;
      let newY = prevPosition.y + velocityRef.current.y * deltaTime;

      // SEMPRE manter posições sub-pixel para movimento ultra-suave
      // O arredondamento (se necessário) será feito apenas na renderização CSS

      // Comportamento ao passar pelo CenterText
      if (!shouldExit) {
        const isInside = isCollidingWithCenterText(newX, newY);
        const now = Date.now();

        if (centerTextBehavior === 'fade') {
          // Fading: só muda opacidade
          if (isInside !== isInsideCenterTextRef.current && now - lastOpacityChangeRef.current > 100) {
            isInsideCenterTextRef.current = isInside;
            lastOpacityChangeRef.current = now;
            if (!isEntering && !isExiting && !isTransitioning) {
              const newOpacity = isInside ? 0.1 : baseOpacityRef.current;
              setOpacity(newOpacity);
            }
          }
        } else if (centerTextBehavior === 'avoid' && isInside) {
          // Evitar: rebater imediatamente ao tentar entrar
          // Rebater na direção oposta ao centro do CenterText
          const centerX = centerTextBounds?.centerX ?? window.innerWidth / 2;
          const centerY = centerTextBounds?.centerY ?? window.innerHeight / 2;
          const dx = newX + photoSize.current / 2 - centerX;
          const dy = newY + photoSize.current / 2 - centerY;
          // Normalizar direção
          const mag = Math.sqrt(dx * dx + dy * dy) || 1;
          // Rebater velocidade e posição - mais suave
          const bounceForce = 0.8; // Reduzir força do bounce
          velocityRef.current.x = (dx / mag) * Math.abs(velocityRef.current.x) * bounceForce;
          velocityRef.current.y = (dy / mag) * Math.abs(velocityRef.current.y) * bounceForce;
          targetVelocityRef.current.x = velocityRef.current.x;
          targetVelocityRef.current.y = velocityRef.current.y;
          // Empurrar para fora de forma mais suave
          newX = prevPosition.x + (dx / mag) * 2; // Empurrar gradualmente
          newY = prevPosition.y + (dy / mag) * 2;
        }
      }

      // Verificar colisões com as bordas da tela e fazer "bounce"
      const photoSize1 = photoSize.current;
      const totalHeight = photoSize1 + 40; // foto + espaçamento + nome aproximado
      let bounceX = newX;
      let bounceY = newY;

      if (newX <= 0 || newX >= window.innerWidth - photoSize1) {
        velocityRef.current.x *= -0.9; // Mantém mais velocidade no bounce
        targetVelocityRef.current.x *= -0.9;
        bounceX = newX <= 0 ? 0 : window.innerWidth - photoSize1;
      }

      if (newY <= 0 || newY >= window.innerHeight - totalHeight) {
        velocityRef.current.y *= -0.9; // Mantém mais velocidade no bounce
        targetVelocityRef.current.y *= -0.9;
        bounceY = newY <= 0 ? 0 : window.innerHeight - totalHeight;
      }

      const proposedPosition = { x: bounceX, y: bounceY };
      
      // Notificar o PositionManager sobre a mudança de posição e obter correções
      let finalPosition = proposedPosition;
      if (onPositionUpdate) {
        const result = onPositionUpdate(person.id, proposedPosition, velocityRef.current);
        if (result) {
          const correctedPosition = result.position;
          
          // Calcular a distância da correção
          const correctionDistance = Math.sqrt(
            Math.pow(correctedPosition.x - proposedPosition.x, 2) + 
            Math.pow(correctedPosition.y - proposedPosition.y, 2)
          );
          
          // Se a correção é significativa, aplicar interpolação ultra-suave
          if (correctionDistance > 2) { // Threshold balanceado
            if (!isBeingCorrectedRef.current) {
              // Primeira vez sendo corrigida, definir target
              targetPositionRef.current = correctedPosition;
              isBeingCorrectedRef.current = true;
            } else {
              // Atualizar target de forma muito gradual
              const lerpFactor = 0.03; // Muito suave para evitar tremidas
              targetPositionRef.current.x += (correctedPosition.x - targetPositionRef.current.x) * lerpFactor;
              targetPositionRef.current.y += (correctedPosition.y - targetPositionRef.current.y) * lerpFactor;
            }
            
            // Interpolar suavemente em direção ao target
            const smoothLerpFactor = 0.08; // Suavização ultra-suave
            finalPosition.x += (targetPositionRef.current.x - finalPosition.x) * smoothLerpFactor;
            finalPosition.y += (targetPositionRef.current.y - finalPosition.y) * smoothLerpFactor;
            
            // Verificar se chegou próximo ao target
            const targetDistance = Math.sqrt(
              Math.pow(targetPositionRef.current.x - finalPosition.x, 2) + 
              Math.pow(targetPositionRef.current.y - finalPosition.y, 2)
            );
            
            if (targetDistance < 2) { // Threshold mais tolerante
              isBeingCorrectedRef.current = false;
            }
          } else {
            // Correção pequena, aplicar muito suavemente
            const microLerpFactor = 0.2; // Muito suave para evitar tremidas
            finalPosition.x += (correctedPosition.x - finalPosition.x) * microLerpFactor;
            finalPosition.y += (correctedPosition.y - finalPosition.y) * microLerpFactor;
            isBeingCorrectedRef.current = false;
          }
          
          // Aplicar correção de velocidade de forma ultra-suave
          if (result.velocityCorrection) {
            const velocityLerpFactor = 0.05; // Ultra-suave para evitar tremidas
            velocityRef.current.x += (result.velocityCorrection.x - velocityRef.current.x) * velocityLerpFactor;
            velocityRef.current.y += (result.velocityCorrection.y - velocityRef.current.y) * velocityLerpFactor;
            targetVelocityRef.current.x += (result.velocityCorrection.x - targetVelocityRef.current.x) * velocityLerpFactor;
            targetVelocityRef.current.y += (result.velocityCorrection.y - targetVelocityRef.current.y) * velocityLerpFactor;
            
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.002) {
              console.log(`🔄 [FloatingPhoto] Velocidade suavizada para ${person.name}: v(${velocityRef.current.x.toFixed(1)}, ${velocityRef.current.y.toFixed(1)})`);
            }
          }
        } else {
          // Não há correção, resetar estado
          isBeingCorrectedRef.current = false;
        }
      }

      return finalPosition;
    });

    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    // Se já temos uma posição gerenciada, usar ela
    if (managedPosition) {
      setPosition(managedPosition);
    } else {
      // Fallback: definir posição inicial segura apenas se não há posição gerenciada
      const safePosition = generateSafeInitialPosition();
      setPosition(safePosition);
    }
  }, [managedPosition]); // Dependência na posição gerenciada

  useEffect(() => {
    // Só iniciar a animação se a posição já foi definida (não é 0,0)
    if (position.x === 0 && position.y === 0) return;
    
    // Check initial opacity based on position
    const isInside = isCollidingWithCenterText(position.x, position.y);
    isInsideCenterTextRef.current = isInside;
    
    if (!shouldEnter && !isNew) { // Only set opacity if not in entering animation and not new
      baseOpacityRef.current = 1;
      setOpacity(isInside ? 0.1 : 1);
    } else {
      baseOpacityRef.current = 1;
      // Para elementos novos ou que devem entrar, manter opacidade em 0
      // A animação de fade-in irá cuidar da transição
    }

    // Iniciar animação
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [speed, position.x, position.y]); // Adicionar position como dependência

  useEffect(() => {
    // Redimensionamento apenas para fotos novas (isNew=true)
    if (!isNewRef.current) return; // Sair se não for nova
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`📷 Foto nova detectada: ${person.name} - Tamanho atual: ${currentSizePercentage}%`);
    }
    
    // Aguardar 2 segundos após o carregamento para iniciar a transição
    const delayTimer = setTimeout(() => {
      if (!isNewRef.current) return; // Verificar novamente após o delay
      
      // Definir tamanho alvo no range normal
      const targetNormalSize = Math.random() * (maxPhotoSize - minPhotoSize) + minPhotoSize;
      const targetSize = Math.round(targetNormalSize);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔄 Iniciando redimensionamento: ${person.name} - De ${currentSizePercentage}% para ${targetSize}%`);
      }
      
      // Configurar transição
      const startSize = currentSizePercentage;
      const startTime = Date.now();
      const duration = 2000; // 2 segundos
      let animationId: number;
      
      // Função de animação suave usando requestAnimationFrame
      const animateResize = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        
        if (elapsed < duration) {
          // Calcular progresso com easing muito suave (ease-out cubic)
          const progress = elapsed / duration;
          const easedProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out mais suave
          
          // Interpolar entre tamanho inicial e final
          const newSize = startSize + (targetSize - startSize) * easedProgress;
          
          // Aplicar o novo tamanho
          setCurrentSizePercentage(newSize); // Não arredondar aqui para suavidade máxima
          
          // Continuar animação
          animationId = requestAnimationFrame(animateResize);
        } else {
          // Transição completa
          setCurrentSizePercentage(targetSize);
          isNewRef.current = false; // Marcar como processada
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Redimensionamento completo: ${person.name} - Tamanho final: ${targetSize}%`);
          }
        }
      };
      
      // Iniciar animação
      animationId = requestAnimationFrame(animateResize);
      
      // Cleanup function para cancelar animação se necessário
      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
      
    }, newPhotoHighlightTime * 1000); // Delay de 2 segundos

    return () => {
      clearTimeout(delayTimer);
    };
  }, [maxPhotoSize, minPhotoSize, person.name]);

  // Calcular tamanho da foto com mais precisão durante transições
  const calculatePhotoSize = () => {
    return (currentSizePercentage / 100) * window.innerHeight;
  };
  
  photoSize.current = calculatePhotoSize();

  useEffect(() => {
    // Animação de fade-in para elementos novos ou que devem entrar
    // IMPORTANTE: Só iniciar fade-in após a imagem estar carregada
    if ((shouldEnter || isNew) && isEntering && imageLoaded) {
        const startTime = Date.now();
        const duration = 1500; // 1.5 segundos para entrada mais suave e visível
        setIsTransitioning(true);        
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`🎭 Iniciando fade-in: ${person.name} - Duração: ${duration}ms (imagem carregada)`);
        }
        
        const fadeIn = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Usar uma curva de easing mais suave (ease-out cubic)
          const easedProgress = 1 - Math.pow(1 - progress, 2); // Quadratic ease-out para entrada mais suave
          
          // Determine target opacity based on whether inside CenterText
          const targetOpacity = isInsideCenterTextRef.current ? 0.1 : baseOpacityRef.current;
          const newOpacity = easedProgress * targetOpacity;
          setOpacity(newOpacity);
          
          if (progress < 1) {
            requestAnimationFrame(fadeIn);
          } else {
            // Transição completa
            setIsTransitioning(false);
            setIsEntering(false);
            // Ensure correct opacity after entering animation
            const finalOpacity = isInsideCenterTextRef.current ? 0.1 : baseOpacityRef.current;
            setOpacity(finalOpacity);
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ Fade-in completo: ${person.name} - Opacidade final: ${finalOpacity}`);
            }
          }
        };
        
        requestAnimationFrame(fadeIn);
    }
  }, [shouldEnter, isNew, isEntering, isExiting, person.name, imageLoaded]);

  // Effect to ensure opacity consistency - but not during exit animations
  useEffect(() => {
    // Only adjust opacity if not in ANY animation states (including exit)
    if (!isEntering && !isExiting && !isTransitioning && !shouldExit) {
      const expectedOpacity = isInsideCenterTextRef.current ? 0.1 : baseOpacityRef.current;
      if (Math.abs(opacity - expectedOpacity) > 0.05) {
        setOpacity(expectedOpacity);
      }
    }
  }, [position.x, position.y, isEntering, isExiting, isTransitioning, shouldExit, opacity, person.name]);


  useEffect(() => {
    if (shouldExit && !isExiting && !hasExitedRef.current) {
      setIsExiting(true);
      setIsTransitioning(true);
      
      // Capturar a opacidade atual no momento que inicia a saída
      const currentOpacity = opacity;
      
      // Iniciar transição suave de opacidade para 0 em 1.5 segundos (mais visível)
      const startTime = Date.now();
      const duration = 1500; // 1.5 segundos para ser mais visível
      
      const fadeOut = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Interpolação linear de opacidade
        const newOpacity = currentOpacity * (1 - progress);
        setOpacity(newOpacity);
        
        if (progress < 1 && !hasExitedRef.current) {
          requestAnimationFrame(fadeOut);
        } else {
          // Transição completa
          setIsTransitioning(false);
          setOpacity(0); // Ensure final opacity is 0
          
          // Pequeno delay para garantir que a animação CSS seja aplicada
          setTimeout(() => {
            if (onExitComplete && !hasExitedRef.current) {
              hasExitedRef.current = true;
              onExitComplete(person.id);
            }
          }, 200); // Delay um pouco maior para garantir visibilidade
        }
      };
      
      requestAnimationFrame(fadeOut);
      
      // Chamar callback após tempo suficiente para sair (backup)
      setTimeout(() => {
        if (!hasExitedRef.current && onExitComplete) {
          hasExitedRef.current = true;
          setOpacity(0);
          onExitComplete(person.id);
        }
      }, 2500); // Ajustado para o novo timing
    }
  }, [shouldExit, person.id, person.name, onExitComplete]); // Removida dependência de opacity


  return (
    <div
      style={{
        position: 'fixed',
        // Usar transform para posicionamento sub-pixel ultra-suave
        left: 0,
        top: 0,
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 999,
        cursor: 'pointer',
        opacity: opacity,
        transition: isExiting 
          ? `opacity ${qualitySettings.opacityTransitionClass.split(' ')[1]} ease-out` 
          : isEntering || isTransitioning
            ? 'opacity 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)' // Transição mais suave para entrada
            : qualitySettings.opacityTransitionClass,
        // Otimizações para suavidade máxima de movimento
        willChange: 'transform, opacity', // Informa ao browser sobre mudanças
        backfaceVisibility: 'hidden',     // Evita flickering
        transformStyle: 'preserve-3d',    // Preserva qualidade 3D
        imageRendering: 'auto',           // Renderização suave automática
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: `${photoSize.current}px`,  // Usar valor preciso sem arredondamento
          height: `${photoSize.current}px`, // Usar valor preciso sem arredondamento
          position: 'relative',
          borderRadius: `${photoSize.current / 2}px`, // Raio preciso para círculo perfeito
          overflow: 'hidden',
          opacity: 1,
          // boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          border: '3px solid rgb(255, 255, 255)', // Borda laranja
          // Propriedades para máxima qualidade visual e suavidade
          willChange: 'transform', // Otimização de rendering
          backfaceVisibility: 'hidden', // Evita flickering
          transform: 'translate3d(0, 0, 0)', // Força aceleração por hardware
          transformStyle: 'preserve-3d', // Preserva qualidade 3D
          imageRendering: 'auto', // Renderização suave para o container
          // Anti-aliasing e suavização máxima
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          // Suavização de redimensionamento sem saltos
          transition: animationQuality === 'high' 
            ? 'transform 0.05s cubic-bezier(0.4, 0, 0.2, 1)' 
            : 'none',
        }}
      >
        <img
          src={person.photo_url}
          alt={person.name}
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            console.error(`❌ Erro ao carregar imagem: ${person.photo_url}`);
            // Notificar o pai para remover este participante
            if (onImageError) {
              onImageError(person.id);
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: 2,
            // Máxima qualidade de imagem e suavidade
            imageRendering: 'auto', // Renderização automática de alta qualidade
            filter: 'none', // Sem filtros para máxima nitidez
            backfaceVisibility: 'hidden', // Evita flickering
            transform: 'translateZ(0)', // Força camada de composição
            transformStyle: 'preserve-3d', // Preserva qualidade 3D
            // Anti-aliasing premium
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          }}
        />
        
        {/* Moldura sobre a foto */}
        {/* {(() => {
          const frameDims = getFrameDimensions();
          return (
            <img
              src={getImageUrl('moldura_foto.webp')}
              alt="Moldura"
              style={{
                position: 'absolute',
                top: `${frameDims.top}px`, // Usar valor preciso
                left: `${frameDims.left}px`, // Usar valor preciso
                width: `${frameDims.width}px`, // Usar valor preciso
                height: `${frameDims.height}px`, // Usar valor preciso
                objectFit: 'cover',
                objectPosition: 'center',
                pointerEvents: 'none',
                zIndex: 1,
                // Otimizações para a moldura
                imageRendering: 'auto', // Sempre usar renderização suave
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)',
                willChange: 'transform',
                transformStyle: 'preserve-3d',
                // Anti-aliasing
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
              }}
              onError={() => console.error('Erro ao carregar moldura:', getImageUrl('moldura_foto.png'))}
            />
          );
        })()} */}
      </div>
      <div
        style={{
          marginTop: '8px',
          padding: '4px 8px',
          backgroundColor: 'transparent',
          color: 'white',
          borderRadius: '12px',
          fontSize: `${photoSize.current * (participantNameFontSize / 100)}px`, // Tamanho de fonte fluído (totalmente configurável via parâmetro)
          fontWeight: '560',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          maxWidth: `${photoSize.current * 2}px`, // Largura fluída
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFamily: "AvenirNextWorld-Regular",
          boxShadow: 'None',
          // Otimizações de texto para máxima suavidade
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)', // Força aceleração por hardware
          transformStyle: 'preserve-3d', // Preserva qualidade 3D do texto
          // Suavização de redimensionamento de texto
          transition: 'font-size 0.1s ease-out',
        }}
      >
        {person.name}
      </div>
    </div>
  );
};

export default FloatingPhoto;
